import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from "@angular/core";
import { CustomModal } from "@modules/core/components/base-modals";
import { ModalHeader } from "@models/modal_step";
import { TranslationService } from "@core-services/translation.service";
import { KycModalFlowStep, Step } from "@modules/kyc/domain/entities/kyc-modal-flow-step.model";
import { KycService } from "@modules/kyc/services/kyc.service";
import { Validateable } from "@models";
import {
	KYC_EDIT_DETAILS_CANCEL_BUTTON_CLICK_EVENT,
	KYC_EDIT_DETAILS_CLOSE_BUTTON_CLICK_EVENT,
	KYC_EDIT_DETAILS_CONFIRM_BUTTON_CLICK_EVENT,
	KYC_EDIT_DETAILS_NO_TOGGLE_VIEWED,
	KYC_EDIT_DETAILS_TOGGLE_VIEWED,
	KYC_SUCCESS_BACK_BUTTON_CLICK_EVENT,
	KYC_SUCCESS_CLOSE_BUTTON_CLICK_EVENT
} from "@helpers/constants";
import { SegmentService } from "@main-services/segment.service";
import { Observable } from "rxjs";
import { RudderstackService } from "@main-services/rudderstack.service";

@Component({
	selector: "kyc-modal",
	templateUrl: "./kyc-modal.component.html",
	styleUrls: ["./kyc-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class KycModalComponent extends CustomModal {
	private readonly kycService = inject(KycService);
	public readonly steps = new Map<KycModalFlowStep, Step>([
		[
			KycModalFlowStep.DATA_UPDATE,
			{
				title: "kyc_up_to_date_question",
				button: "summary_button_confirm",
				buttonAction: () => this.goToNextStepConfirm()
			}
		],
		[
			KycModalFlowStep.SUCCESS,
			{ title: "kyc_information_update", button: "fma_back_overview", buttonAction: () => this.goToPrevStep() }
		]
	]);

	public readonly shouldForceKycFlow$: Observable<boolean> = this.kycService.shouldForceKycFlow$();
	public KycModalFlowStep: typeof KycModalFlowStep = KycModalFlowStep;
	public title: string = "";
	public header: ModalHeader = null;
	public step: KycModalFlowStep = KycModalFlowStep.DATA_UPDATE;
	public editClicked: boolean = false;
	public slider: KycEditSlider = new KycEditSlider();
	private dataConfirmed: boolean = false;

	public constructor(
		protected translationService: TranslationService,
		private ref: ChangeDetectorRef,
		private readonly segmentService: SegmentService,
		private readonly rudderstackService: RudderstackService
	) {
		super();
	}

	public getStep(): Step {
		return this.steps.get(this.step);
	}

	public handleButtonClick(): void {
		if (this.isSliderAvailable() && this.isButtonDisabled()) {
			this.slider.wiggle("enabled");
			return;
		}

		if (this.step === KycModalFlowStep.DATA_UPDATE) {
			this.segmentService.track({ name: KYC_EDIT_DETAILS_TOGGLE_VIEWED });
			this.rudderstackService.track({ name: KYC_EDIT_DETAILS_TOGGLE_VIEWED });
			if (this.editClicked) {
				this.segmentService.track({ name: KYC_SUCCESS_BACK_BUTTON_CLICK_EVENT });
				this.segmentService.track({ name: KYC_EDIT_DETAILS_CLOSE_BUTTON_CLICK_EVENT });
				this.rudderstackService.track({ name: KYC_SUCCESS_BACK_BUTTON_CLICK_EVENT });
				this.rudderstackService.track({ name: KYC_EDIT_DETAILS_CLOSE_BUTTON_CLICK_EVENT });
			} else {
				this.segmentService.track({ name: KYC_EDIT_DETAILS_CONFIRM_BUTTON_CLICK_EVENT });
				this.rudderstackService.track({ name: KYC_EDIT_DETAILS_CONFIRM_BUTTON_CLICK_EVENT });
			}
		} else if (this.step === KycModalFlowStep.SUCCESS) {
			if (this.editClicked) {
				this.segmentService.track({ name: KYC_EDIT_DETAILS_NO_TOGGLE_VIEWED });
				this.rudderstackService.track({ name: KYC_EDIT_DETAILS_NO_TOGGLE_VIEWED });
			}
		}

		this.getStep().buttonAction();
	}

	private goToNextStep(): void {
		this.step += 1;
		this.ref.detectChanges();
	}

	private goToPrevStep(): void {
		if (this.dataConfirmed) {
			this.close();
			return;
		}
		this.step -= 1;
		this.ref.detectChanges();
	}
	private goToNextStepConfirm(): void {
		if (this.editClicked) {
			this.goToNextStep();
		} else {
			this.kycService.confirmUserData().subscribe((res) => {
				if (res) {
					this.dataConfirmed = true;
					this.kycService.removeReviewDate(); // the banner disappears from dashboard
					this.goToNextStep();
				}
			});
		}
	}

	public onEditClick(val: boolean): void {
		this.editClicked = val;
	}

	public onToggleClick(): void {
		this.editClicked = false;
	}

	public getButtonTranslation(): string {
		if ((this.editClicked && this.step !== KycModalFlowStep.SUCCESS) || this.dataConfirmed) {
			return "close";
		}
		return this.getStep().button;
	}

	public isSliderAvailable(): boolean {
		return !this.editClicked;
	}

	public isButtonDisabled(): boolean {
		return !this.slider.enabled && this.step === KycModalFlowStep.DATA_UPDATE && !this.editClicked;
	}

	public closeKycModal(): void {
		if (this.step === this.KycModalFlowStep.DATA_UPDATE) {
			this.segmentService.track({ name: KYC_EDIT_DETAILS_CANCEL_BUTTON_CLICK_EVENT });
			this.rudderstackService.track({ name: KYC_EDIT_DETAILS_CANCEL_BUTTON_CLICK_EVENT });
		} else if (this.step === this.KycModalFlowStep.SUCCESS) {
			this.segmentService.track({ name: KYC_SUCCESS_CLOSE_BUTTON_CLICK_EVENT });
			this.rudderstackService.track({ name: KYC_SUCCESS_CLOSE_BUTTON_CLICK_EVENT });
		}
		this.closeModal();
	}

	public closeModal(): void {
		this.modalService.popAll();
	}
}

class KycEditSlider extends Validateable {
	public enabled: boolean = false;
}
